import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  Package,
  Settings,
  Stethoscope,
  Sparkles,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '@clinic-os/ui';
import { useLocation, Link } from 'react-router-dom';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/patients', label: 'Patients', icon: Users },
  { path: '/appointments', label: 'Appointments', icon: Calendar },
  { path: '/invoices', label: 'Invoices', icon: FileText },
  { path: '/inventory', label: 'Inventory', icon: Package },
  { path: '/ai', label: 'AI Notes', icon: Sparkles },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export function DashboardLayout() {
  const location = useLocation();
  // Below lg the sidebar is an off-canvas drawer opened from the top bar.
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div className="flex h-screen flex-col bg-slate-50 dark:bg-slate-900 lg:flex-row">
      {/* Top bar (below lg only) */}
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900 lg:hidden">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation"
          aria-expanded={navOpen}
          aria-controls="app-sidebar-drawer"
          data-testid="mobile-nav-toggle"
          className="-ml-2 inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <Menu className="h-6 w-6" />
        </button>
        <span className="text-lg font-semibold text-slate-900 dark:text-white">MedFlow</span>
      </header>

      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
          data-testid="mobile-nav-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        id="app-sidebar-drawer"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-shrink-0 transform flex-col border-r border-slate-200 bg-white transition-transform dark:border-slate-800 dark:bg-slate-900 lg:static lg:visible lg:translate-x-0',
          navOpen ? 'visible translate-x-0' : 'invisible -translate-x-full'
        )}
        data-appdom-id="app-sidebar"
        data-appdom-type="Sidebar"
        data-appdom-scope="nav"
      >
        <div
          className="flex items-center gap-2 px-6 py-5 border-b border-slate-100 dark:border-slate-800"
          data-appdom-id="app-sidebar-brand"
          data-appdom-type="Header"
          data-appdom-scope="header"
        >
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-semibold text-slate-900 dark:text-white">MedFlow</span>
        </div>

        {navOpen && (
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
            className="absolute right-2 top-3 inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <nav
          aria-label="Main"
          className="flex-1 overflow-y-auto px-3 py-4 space-y-1"
          data-appdom-id="app-nav"
          data-appdom-type="NavBar"
          data-appdom-scope="nav"
        >
          {NAV_ITEMS.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-appdom-id={`app-nav-${item.path.replace(/^\//, '')}`}
                data-appdom-type="Link"
                data-appdom-scope="nav"
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                )}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div
          className="px-4 py-4 border-t border-slate-100 dark:border-slate-800"
          data-appdom-id="app-sidebar-user"
          data-appdom-type="Card"
          data-appdom-scope="nav"
        >
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 dark:bg-slate-800">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              DR
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">Dr. Smith</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Dental Clinic</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main tabIndex={0} aria-label="Content" className="min-w-0 flex-1 overflow-y-auto focus-visible:outline-none bg-slate-50 dark:bg-slate-900">
        <Outlet />
      </main>
    </div>
  );
}
